import axios from 'axios';
import { accountingApi } from '../../../../config/apiClient';
import { ACCOUNTING_API } from '../../../../config/api';

/**
 * Stok kartı ambalajları (kutu, paket, koli, palet…) ve ek barkodları.
 * İstek gövdeleri PascalCase; yanıt alanları camelCase okunur. CompanyId gönderilmez.
 */

export interface PackagingUnit {
  id?: string;
  unitName: string;
  factor: number;
  containsQuantity: number | null;
  containsUnitName: string | null;
  salePrice: number | null;
  effectiveSalePrice: number | null;
  isDefaultForProduction: boolean;
  sortOrder: number;
  recipeUsageCount: number;
  barcodes: string[];
}

export interface PackagingCard {
  itemId: string;
  baseUnit: string;
  mainBarcode: string | null;
  itemSalePrice: number;
  baseBarcodes: string[];
  units: PackagingUnit[];
}

export interface BarcodeMatch {
  itemId: string;
  itemName: string;
  stockCode: string;
  baseUnit: string;
  unitName: string | null;
  isPackage: boolean;
  isMainBarcode: boolean;
  factor: number;
  effectiveSalePrice: number;
}

export interface BarcodeResolution {
  code: string;
  found: boolean;
  ambiguous: boolean;
  matches: BarcodeMatch[];
}

export interface BarcodeConflict {
  barcode: string;
  itemId: string;
  itemName: string | null;
  unitName: string | null;
}

export class PackagingError extends Error {
  code?: string;
  conflicts: BarcodeConflict[];
  usages: { unitName: string; count: number }[];

  constructor(message: string, code?: string, conflicts: BarcodeConflict[] = [], usages: { unitName: string; count: number }[] = []) {
    super(message);
    this.name = 'PackagingError';
    this.code = code;
    this.conflicts = conflicts;
    this.usages = usages;
  }
}

const fallbackMessage = 'Beklenmeyen bir hata oluştu.';

type Rec = Record<string, unknown>;
const asRecord = (value: unknown): Rec | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Rec) : null;
const text = (value: unknown): string => (typeof value === 'string' ? value : '');
const num = (value: unknown, fallback = 0): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};
const numOrNull = (value: unknown): number | null => (value == null || value === '' ? null : num(value, NaN));
const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

function mapUnit(raw: unknown): PackagingUnit | null {
  const row = asRecord(raw);
  const unitName = text(row?.unitName).trim();
  if (!row || !unitName) return null;
  return {
    id: text(row.id) || undefined,
    unitName,
    factor: num(row.factor),
    containsQuantity: numOrNull(row.containsQuantity),
    containsUnitName: text(row.containsUnitName) || null,
    salePrice: numOrNull(row.salePrice),
    effectiveSalePrice: numOrNull(row.effectiveSalePrice),
    isDefaultForProduction: row.isDefaultForProduction === true,
    sortOrder: num(row.sortOrder),
    recipeUsageCount: num(row.recipeUsageCount),
    barcodes: strings(row.barcodes),
  };
}

function mapCard(raw: unknown): PackagingCard {
  const body = asRecord(raw) ?? {};
  return {
    itemId: text(body.itemId),
    baseUnit: text(body.baseUnit),
    mainBarcode: text(body.mainBarcode) || null,
    itemSalePrice: num(body.itemSalePrice),
    baseBarcodes: strings(body.baseBarcodes),
    units: Array.isArray(body.units)
      ? body.units.map(mapUnit).filter((unit): unit is PackagingUnit => unit != null)
      : [],
  };
}

function toError(error: unknown): PackagingError {
  if (error instanceof PackagingError) return error;
  if (axios.isAxiosError(error)) {
    const body = asRecord(error.response?.data);
    if (body) {
      const data = Array.isArray(body.data) ? body.data : [];
      const conflicts = data.flatMap((item) => {
        const row = asRecord(item);
        const barcode = text(row?.barcode);
        return row && barcode
          ? [{ barcode, itemId: text(row.itemId), itemName: text(row.itemName) || null, unitName: text(row.unitName) || null }]
          : [];
      });
      const usages = data.flatMap((item) => {
        const row = asRecord(item);
        const unitName = text(row?.unitName);
        return row && unitName && row.count != null ? [{ unitName, count: num(row.count) }] : [];
      });
      return new PackagingError(text(body.message) || fallbackMessage, text(body.code) || undefined, conflicts, usages);
    }
  }
  if (error instanceof Error && error.message) return new PackagingError(error.message);
  return new PackagingError(fallbackMessage);
}

export async function getPackaging(itemId: string): Promise<PackagingCard> {
  try {
    const response = await accountingApi.get(ACCOUNTING_API.INVENTORY_PACKAGING_ITEM(itemId));
    const body = asRecord(response.data);
    if (!body || body.success === false || body.data === undefined) {
      throw new PackagingError(text(body?.message) || fallbackMessage, text(body?.code) || undefined);
    }
    return mapCard(body.data);
  } catch (error) {
    throw toError(error);
  }
}

export async function savePackaging(itemId: string, payload: unknown): Promise<PackagingCard> {
  try {
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_PACKAGING_SAVE(itemId), payload);
    const body = asRecord(response.data);
    if (!body || body.success === false) {
      throw new PackagingError(text(body?.message) || fallbackMessage, text(body?.code) || undefined);
    }
    return mapCard(body.data);
  } catch (error) {
    throw toError(error);
  }
}

/** Barkodları ürün ve ambalaja çevirir. Bulunamayan barkod found=false döner. */
export async function resolveBarcodes(codes: string[]): Promise<BarcodeResolution[]> {
  try {
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_PACKAGING_RESOLVE, { Codes: codes });
    const body = asRecord(response.data);
    const data = Array.isArray(body?.data) ? body!.data as unknown[] : [];
    return data.flatMap((item) => {
      const row = asRecord(item);
      if (!row) return [];
      const matches = Array.isArray(row.matches) ? row.matches : [];
      return [{
        code: text(row.code),
        found: row.found === true,
        ambiguous: row.ambiguous === true,
        matches: matches.flatMap((raw) => {
          const m = asRecord(raw);
          if (!m) return [];
          return [{
            itemId: text(m.itemId),
            itemName: text(m.itemName),
            stockCode: text(m.stockCode),
            baseUnit: text(m.baseUnit),
            unitName: text(m.unitName) || null,
            isPackage: m.isPackage === true,
            isMainBarcode: m.isMainBarcode === true,
            factor: num(m.factor, 1),
            effectiveSalePrice: num(m.effectiveSalePrice),
          }];
        }),
      }];
    });
  } catch (error) {
    throw toError(error);
  }
}

/** Çakışma mesajı: "'X' barkodu Fanta 330 ml · Koli kartında kullanılıyor." */
export function describeConflict(code: string, match: { itemName: string | null; unitName: string | null }): string {
  const where = match.unitName ? `${match.itemName ?? ''} · ${match.unitName}` : (match.itemName ?? '');
  return `'${code}' barkodu ${where} kartında kullanılıyor.`;
}
